'use client'
import * as React from 'react'
import { ToolProgress } from '@/components/tools/tool-progress'
import { Check, X, AlertTriangle, Loader2, ArrowRight, Link2, Code2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NewsletterCapture } from '@/components/marketing/newsletter-capture'

type CheckStatus = 'pass' | 'warn' | 'fail'

type ValidationCheck = {
  id: string
  label: string
  status: CheckStatus
  detail: string
}

type ValidationResult = {
  source: 'url' | 'json'
  blocksFound: number
  hadParseError: boolean
  detectedTypes: string[]
  checks: ValidationCheck[]
  eligible: boolean
  passCount: number
  warnCount: number
  failCount: number
}

type Mode = 'url' | 'json'

const SAMPLE_JSONLD = `{
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Airlift High-Waist Suit Up Shorts",
  "sku": "AL-SHORT-001",
  "offers": { "@type": "Offer", "price": "64.76", "priceCurrency": "USD" },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.7",
    "reviewCount": "212"
  },
  "review": [{
    "@type": "Review",
    "author": { "@type": "Person", "name": "Priya S." },
    "datePublished": "2026-02-14",
    "reviewBody": "Held up through a half-marathon, no ride-up.",
    "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
  }]
}`

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === 'pass')
    return (
      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
        <Check className="h-3 w-3" />
      </span>
    )
  if (status === 'warn')
    return (
      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
        <AlertTriangle className="h-3 w-3" />
      </span>
    )
  return (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300">
      <X className="h-3 w-3" />
    </span>
  )
}

export function ReviewSchemaValidator() {
  const [mode, setMode] = React.useState<Mode>('url')
  const [url, setUrl] = React.useState('')
  const [jsonld, setJsonld] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [result, setResult] = React.useState<ValidationResult | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = mode === 'url' ? { url: url.trim() } : { jsonld: jsonld.trim() }
    if (mode === 'url' ? !url.trim() : !jsonld.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch('/api/tools/review-schema-validator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) setError(data?.error || 'Validation failed.')
      else setResult(data.result as ValidationResult)
    } catch {
      setError('Network error. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-3xl">
      <div className="mb-4 inline-flex rounded-lg border bg-card p-1 text-sm">
        <button
          type="button"
          onClick={() => { setMode('url'); setResult(null); setError(null) }}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition ${mode === 'url' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Link2 className="h-3.5 w-3.5" /> From a URL
        </button>
        <button
          type="button"
          onClick={() => { setMode('json'); setResult(null); setError(null) }}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition ${mode === 'json' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Code2 className="h-3.5 w-3.5" /> Paste JSON-LD
        </button>
      </div>

      <form onSubmit={onSubmit} className="rounded-2xl border bg-card p-5 sm:p-6">
        {mode === 'url' ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              type="url"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste a product page URL (e.g. yourbrand.com/products/the-bag)"
              className="h-12 flex-1 text-base"
              aria-label="Product page URL"
            />
            <Button type="submit" size="lg" disabled={loading || !url.trim()} className="h-12 px-6">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating</> : <>Validate <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        ) : (
          <div>
            <label htmlFor="rsv-json" className="text-sm font-medium text-foreground">
              Paste your Product / Review JSON-LD
            </label>
            <textarea
              id="rsv-json"
              value={jsonld}
              onChange={(e) => setJsonld(e.target.value)}
              rows={9}
              placeholder='Paste a JSON-LD object, or a full <script type="application/ld+json">…</script> tag.'
              className="mt-2 w-full resize-y rounded-xl border bg-background p-3.5 font-mono text-[13px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => { setJsonld(SAMPLE_JSONLD); setResult(null) }}
                className="text-left text-xs font-medium text-primary underline underline-offset-4 hover:text-primary/80"
              >
                Load a sample
              </button>
              <Button type="submit" size="lg" disabled={loading || !jsonld.trim()} className="shrink-0">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating</> : <>Validate <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        )}
      </form>

      {loading && (
        <ToolProgress label="your page" steps={[{ title: "Fetching your page", sub: "Loading the HTML" }, { title: "Parsing Review / AggregateRating", sub: "JSON-LD + microdata" }, { title: "Validating", sub: "Required fields, rich-result eligibility" }]} />
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-8 space-y-6">
          <div className={`rounded-2xl border-2 ${result.eligible ? 'border-emerald-500/40' : result.failCount > 0 ? 'border-rose-500/40' : 'border-amber-500/40'} bg-card p-6 sm:p-8`}>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Rich-result eligibility</p>
                <p className={`mt-1 font-display text-2xl font-semibold ${result.eligible ? 'text-emerald-700 dark:text-emerald-300' : result.failCount > 0 ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300'}`}>
                  {result.eligible ? 'Likely eligible' : result.failCount > 0 ? 'Not eligible yet' : 'Eligible with caveats'}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {result.blocksFound} JSON-LD block{result.blocksFound === 1 ? '' : 's'} read
                  {result.hadParseError && ', one or more failed to parse'}
                  {result.detectedTypes.length > 0 && ` · ${result.detectedTypes.join(', ')}`}
                </p>
              </div>
              <div className="flex shrink-0 gap-3 text-center">
                <Tally n={result.passCount} label="pass" tone="text-emerald-600 dark:text-emerald-400" />
                <Tally n={result.warnCount} label="warn" tone="text-amber-600 dark:text-amber-400" />
                <Tally n={result.failCount} label="fail" tone="text-rose-600 dark:text-rose-400" />
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              Field-by-field
            </p>
            <ul className="mt-3 grid gap-3">
              {result.checks.map((c) => (
                <li key={c.id} className="rounded-xl border bg-background p-4">
                  <div className="flex items-start gap-3">
                    <StatusIcon status={c.status} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug">{c.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{c.detail}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border bg-card p-5 sm:p-6">
            <NewsletterCapture
              source="tool:review-schema-validator"
              headline="Get the review-schema cheat sheet + new teardowns"
              caption="One email with the copy-paste Product + Review + AggregateRating template this validator checks against. No drip sequence."
              cta="Send it over"
            />
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Idukki emits valid Product + Review + AggregateRating JSON-LD from your real verified reviews automatically.{' '}
              <a href="https://app.idukki.io/register" className="font-medium text-primary underline underline-offset-4">Start a free trial</a>.
            </p>
          </div>

          <div className="rounded-xl border border-dashed bg-secondary/40 p-4 text-xs text-muted-foreground">
            These checks mirror Google&rsquo;s structured-data requirements for the review snippet. They are deterministic: the same input always returns the same verdict. They do not replace Google&rsquo;s Rich Results Test, which renders JavaScript and is the final word on eligibility.
          </div>
        </div>
      )}
    </div>
  )
}

function Tally({ n, label, tone }: { n: number; label: string; tone: string }) {
  return (
    <div>
      <div className={`font-display text-3xl font-bold tabular-nums ${tone}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  )
}
